import { CountryCode } from '@constants'
import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type AvailableCoupon } from '@hooks-api/types/dataModel/coupon'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { IconButton } from '@pets-ui/IconButton/IconButton'
import { CloseLarge, Coupon, RemindCircle } from '@pets-ui/icons'
import { Paper } from '@pets-ui/Paper/Paper'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { formatDateTimeByCountryCode, getCurrentUsTimezoneType, utcToUsTimezone } from '@utils/timeConverter'
import { addHours } from 'date-fns'
import { useImperativeHandle, useRef, useState, type ForwardedRef, type PropsWithChildren, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { CouponImage } from '../CouponImage/CouponImage'
import cartUiI18nInstance from '../i18n/i18nConfig'
import { CouponListItemChips } from './CouponListItemChips'
import { CouponListItemProgress } from './CouponListItemProgress'
import { CouponListItemStatus } from './CouponListItemStatus'

const styles = stylex.create({
  active: {
    borderColor: colors.yellow600,
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: '1px',
  },
  couponName: {
    flex: '1',
  },
})

export interface CouponListItemHandler {
  scrollIntoView: (arg?: boolean | ScrollIntoViewOptions) => void
}

export interface CouponListItemProps {
  criterionGroups: AvailableCoupon['criterion_groups']
  rootRef?: ForwardedRef<CouponListItemHandler>
  title?: ReactNode
  /**
   * 是否被套用
   */
  isApplied?: boolean
  /**
   * 已達到使用者個人上限
   */
  isReachUserLimit?: boolean
  /**
   * 全部優惠券已經使用完畢
   */
  isRunOut?: boolean
  /**
   * 品項沒有庫存
   */
  isOutOfStock?: boolean
  isManualInput?: boolean
  isNewCustomerOnly?: boolean
  isOldCustomerOnly?: boolean
  startTime?: string | null
  endTime?: string | null
  countryCode?: CountryCode
  couponCode?: string
  /**
   * 已使用次數
   */
  currentUsageCount?: number | null
  /**
   * 使用次數限制
   */
  maxUsageLimit?: number | null
  thumbnailUrl?: string | null
  onManualCouponDelete?: () => void
  isManualCouponSelected?: boolean
  onManualCouponSelectionChange?: (selected: boolean) => void
  /**
   * 唯讀模式：選取/刪除控制 disabled（結帳頁僅能檢視不可編輯）。
   */
  isSelectionDisabled?: boolean
  onGoShopping?: () => void
  goShoppingHref?: string
  isGoShoppingDisabled?: boolean
  isUserLoggedIn?: boolean
  testId?: string
}

export const CouponListItem = (props: PropsWithChildren<CouponListItemProps>) => {
  const {
    rootRef,
    title,
    isApplied,
    isReachUserLimit,
    isRunOut,
    isOutOfStock,
    isManualInput,
    isNewCustomerOnly,
    isOldCustomerOnly,
    criterionGroups,
    startTime,
    endTime,
    children,
    couponCode,
    countryCode,
    currentUsageCount,
    maxUsageLimit,
    thumbnailUrl,
    onManualCouponDelete,
    isManualCouponSelected,
    onManualCouponSelectionChange,
    isSelectionDisabled,
    onGoShopping,
    goShoppingHref,
    isGoShoppingDisabled,
    isUserLoggedIn = true,
    testId,
  } = props
  const [isActive, setIsActive] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const isNotAvailableCoupon = isReachUserLimit || isRunOut || isOutOfStock
  const isNewCoupon = !!startTime && new Date() <= addHours(new Date(startTime), 24)
  const isEnding = !!endTime && new Date(endTime) <= addHours(new Date(), 24)
  const { t } = useTranslation('cartUi', { i18n: cartUiI18nInstance })
  const isUS = countryCode === CountryCode.US
  const currentUsTime = utcToUsTimezone(new Date())
  const isManualSelectionEnabled = isManualInput && !!onManualCouponSelectionChange
  const isManualDeleteEnabled = isManualInput && !!onManualCouponDelete
  /** US 使用 PST 時區 */
  const currentUsTimezoneName = getCurrentUsTimezoneType(currentUsTime)
  const formattedEndTime = endTime
    ? formatDateTimeByCountryCode(endTime, countryCode ?? CountryCode.TW, 'yyyy-MM-dd HH:mm')
    : null

  const scrollIntoView = useCallbackEvent((arg?: boolean | ScrollIntoViewOptions) => {
    containerRef.current?.scrollIntoView(arg)
    setIsActive(true)
  })

  useImperativeHandle(rootRef, () => ({
    scrollIntoView,
  }))

  const getGoShoppingButtonComponent = () => {
    if (!goShoppingHref) return 'button'
    return isGoShoppingDisabled ? 'button' : 'a'
  }

  return (
    <Paper
      data-testid={testId}
      rootRef={containerRef}
      rootStyle={[isActive && styles.active]}
      flexGrow={1}
      borderRadius={8}
      scrollMarginTop={2}
      overflow="clip"
    >
      {isNotAvailableCoupon && <CouponListItemStatus isReachUserLimit={isReachUserLimit} isRunOut={isRunOut} isOutOfStock={isOutOfStock} />}
      <VStack height="100%">
        <HStack height="100%" flexWrap="nowrap">
          <HStack p={4} gap={4} alignItems="center" flexGrow={1}>
            <CouponImage
              criterionGroups={criterionGroups}
              imageUrl={thumbnailUrl || ''}
              variant={isManualInput ? 'ticket' : 'default'}
            />
            <HStack flexWrap="nowrap" flex={1} gap={1}>
              <VStack gap={1} flexGrow={1}>
                <CouponListItemChips isEnding={isEnding} isNewCoupon={isNewCoupon} isNewCustomerOnly={isNewCustomerOnly} isOldCustomerOnly={isOldCustomerOnly} />
                {title
                && (
                  <Text variant="subtitle" color={colors.grey800} rootStyle={styles.couponName}>
                    {title}
                  </Text>
                )}
                {couponCode && (
                  <HStack gap={1}>
                    <Coupon />
                    <Text variant="caption">{couponCode}</Text>
                  </HStack>
                )}
                <Text variant="caption" color={colors.grey500}>
                  {`${t('cartUi.expirationDate', { defaultValue: '使用期限' })}: ${formattedEndTime ? `${formattedEndTime}${isUS ? ` (${currentUsTimezoneName})` : ''}` : t('cartUi.noExpirationDate', { defaultValue: '無期限' })}`}
                </Text>
                {typeof maxUsageLimit === 'number' && typeof currentUsageCount === 'number' && (
                  <CouponListItemProgress maxUsageLimit={maxUsageLimit} currentUsageCount={currentUsageCount} />
                )}
              </VStack>
              {(goShoppingHref || onGoShopping) && (
                <Box display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                  <Button component={getGoShoppingButtonComponent()} href={goShoppingHref} disabled={isGoShoppingDisabled} target="_blank" variant="outlined" size="small" onClick={onGoShopping}>
                    {t('cartUi.goShopping', { defaultValue: '去逛逛' })}
                  </Button>
                </Box>
              )}
            </HStack>
          </HStack>
          {isManualSelectionEnabled && (
            <Box display="flex" alignItems="center" justifyContent="center" width={40} borderLeftWidth={1} borderLeftColor={colors.grey300} borderLeftStyle="dashed">
              <IconButton disabled={isSelectionDisabled} onClick={() => onManualCouponSelectionChange(!isManualCouponSelected)} fontSize={20} width="40px">
                {isManualCouponSelected
                  ? <RemindCircle fontSize={18} color={colors.blue600} />
                  : (
                    <Box
                      borderWidth={1}
                      borderStyle="solid"
                      borderColor={colors.grey600}
                      backgroundColor={colors.white}
                      borderRadius={50}
                      width={16}
                      height={16}
                    />
                  )}
              </IconButton>
            </Box>
          )}
          {isManualDeleteEnabled && (
            // NOTE: z-index 是為了在優惠券無法使用時也可以 delete
            <Box zIndex={1} display="flex" alignItems="center" justifyContent="center" width={40} borderLeftWidth={1} borderLeftColor={colors.grey300} borderLeftStyle="dashed">
              <IconButton disabled={isSelectionDisabled} onClick={onManualCouponDelete} fontSize={16} width="40px">
                <CloseLarge />
              </IconButton>
            </Box>
          )}
        </HStack>
        {isUserLoggedIn && children && (
          <Box p={2} borderTopWidth={1} borderTopStyle="dashed" borderTopColor={colors.grey200} backgroundColor={isApplied ? colors.white : colors.yellow50} borderBottomLeftRadius={8} borderBottomRightRadius={8}>
            {children}
          </Box>
        )}
      </VStack>
    </Paper>
  )
}
